import { cn } from '@lib/cn'
import { useBodyScrollLock } from '@lib/hooks/useBodyScrollLock'
import { NavigationContent } from '@lib/kontent/navigation-helpers/normalize-navigation-content'
import { motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import HeaderMobileNavFooter from './Footer'
import { HeaderMobileNavHouseOfGassan } from './HouseOfGassan'
import Row from './Row'
import HeaderMobileSubNav from './SubNav'
import Shop from './shop'
import Diamonds from './shop/Diamonds'
import { ROW_HEIGHT_MEDIUM, ROW_HEIGHT_SMALL } from './utils/config'
import { useResponsiveRowHeight } from './utils/useResponsiveRowHeight'

import PreOwned from './pre-owned'

const getAnimation = ({
  index,
  active,
  height,
}: {
  index: number
  active: number | null
  height: number
}) => {
  if (active === null) {
    return {
      animate: { y: 0, opacity: 1 },
      transition: { ease: 'easeOut', duration: 0.1 },
    } as const
  }
  if (index === active) {
    return {
      animate: { y: 0 - index * height, opacity: 2 },
      transition: { ease: 'easeOut', duration: 0.2, delay: 0.2 },
    } as const
  } else {
    return {
      animate: { opacity: 0 },
      transition: { ease: 'easeOut', duration: 0.2 },
    } as const
  }
}
type Props = PropsWithChildren<{
  content: NavigationContent
  isVisible: boolean
}>

const MobileNav: FC<Props> = ({ content, isVisible }) => {
  const [active, setActive] = useState(null)
  const [isMounted, setMounted] = useState(false)
  const rowHeight = useResponsiveRowHeight(ROW_HEIGHT_SMALL, ROW_HEIGHT_MEDIUM)
  const ref = useRef()
  const router = useRouter()
  const {
    t,
    i18n: { language },
  } = useTranslation(['other', 'navigation'])

  const toggle = (index) => {
    setActive(active === index ? null : index)
  }

  useEffect(() => {
    if (isVisible) {
      setMounted(true)
    }
    return () => {
      setMounted(false)
    }
  }, [isVisible])

  useBodyScrollLock(ref, isMounted)

  return (
    <div
      className={cn(
        '-webkit-overflow-scrolling-touch absolute h-auto w-full overflow-y-auto',
        'min-h-[calc(100dvh-48px)]',
        'md:min-h-[calc(100dvh-104px)]',
        !isVisible && 'hidden',
      )}
      ref={ref}
    >
      <Row
        {...getAnimation({ index: 0, active: active, height: rowHeight })}
        hasChildren={false}
        onClick={() => {
          router.push(`/${language}/rolex`, `/${language}/rolex`)
        }}
        label="ROLEX"
        isActive={false}
      >
        ROLEX
      </Row>
      <Row
        {...getAnimation({ index: 1, active: active, height: rowHeight })}
        label="Rolex Certified Pre-Owned"
        onClick={() => router.push(`/${language}${t('rcpo.index.url', { ns: 'navigation' })}`)}
        isActive={false}
        hasChildren={false}
        style={{ top: 1 * rowHeight }}
      >
        Rolex Certified Pre-Owned
      </Row>
      <Row
        {...getAnimation({ index: 2, active: active, height: rowHeight })}
        label="Shop"
        onClick={() => toggle(2)}
        isActive={active === 2}
        style={{ top: 2 * rowHeight }}
      >
        <Shop content={content} />
      </Row>
      <Row
        {...getAnimation({ index: 3, active: active, height: rowHeight })}
        label="Pre-owned"
        onClick={() => toggle(3)}
        isActive={active === 3}
        style={{ top: 3 * rowHeight }}
      >
        <PreOwned content={content} />
      </Row>
      <Row
        {...getAnimation({ index: 4, active: active, height: rowHeight })}
        label={t('mainNav.diamonds', { ns: 'navigation' })}
        onClick={() => toggle(4)}
        isActive={active === 4}
        style={{ top: 4 * rowHeight }}
      >
        <motion.div className={cn('bg-pampas-light h-full px-4 py-6 md:p-8')}>
          <Diamonds {...content.diamonds} variant="extended" />
        </motion.div>
      </Row>
      <Row
        {...getAnimation({ index: 5, active: active, height: rowHeight })}
        label={t('mainNav.houseOfGassan', { ns: 'navigation' })}
        onClick={() => toggle(5)}
        isActive={active === 5}
        style={{ top: 5 * rowHeight }}
      >
        <motion.div className="h-full bg-pampasLight px-4 py-6 md:p-8">
          <HeaderMobileNavHouseOfGassan columns={content.houseOfGassan} />
        </motion.div>
      </Row>
      <Row
        {...getAnimation({ index: 6, active: active, height: rowHeight })}
        label="Tours"
        onClick={() => router.push(`/${language}${t('tours', { ns: 'navigation' })}`)}
        isActive={false}
        hasChildren={false}
        style={{ top: 6 * rowHeight }}
      >
        Tours
      </Row>
      <HeaderMobileSubNav isVisible={active === null} style={{ top: 7 * rowHeight }} />
      <HeaderMobileNavFooter />
    </div>
  )
}

export default MobileNav
